import { SnapshotStore } from "./snapshotStore";

export const SERVICE_WORKER_URL = "/service-worker.js";

/** Как часто искать новую версию у открытого приложения. */
const CHECK_INTERVAL_MS = 60 * 60 * 1000;
/** Сколько ждать смены service worker после команды «обновить», прежде чем сдаться. */
const APPLY_TIMEOUT_MS = 10_000;

export type UpdateStatus =
  /** Работает актуальная версия. */
  | "idle"
  /** Новая версия скачана и ждёт решения человека. */
  | "available"
  /** Человек согласился; ждём смены service worker и перезагружаем страницу. */
  | "applying"
  /** Другая вкладка уже обновила приложение; эта работает на старом коде. */
  | "reload-recommended";

export interface UpdateSnapshot {
  /** Браузер умеет service worker и страница открыта в защищённом контексте. */
  supported: boolean;
  /** Service worker зарегистрирован. */
  registered: boolean;
  status: UpdateStatus;
  checking: boolean;
  /** Когда в последний раз успешно спросили сервер о новой версии. */
  lastCheckedAt: number | null;
}

export type ServiceWorkerContainerLike = Pick<
  ServiceWorkerContainer,
  "register" | "controller" | "addEventListener" | "removeEventListener"
>;

export interface ServiceWorkerUpdaterOptions {
  container?: ServiceWorkerContainerLike;
  /** Перезагрузка страницы после смены версии; в тестах подменяется. */
  reload?: () => void;
  target?: Pick<Window, "addEventListener" | "removeEventListener">;
}

/** Регистрация единственного service worker; ею же пользуется и Web Push. */
export function registerServiceWorker(
  container: Pick<ServiceWorkerContainer, "register">,
): Promise<ServiceWorkerRegistration> {
  // updateViaCache: "none" — браузер не берёт сам файл service worker из
  // HTTP-кеша, иначе новая версия доходила бы до устройств с задержкой.
  return container.register(SERVICE_WORKER_URL, {
    scope: "/",
    updateViaCache: "none",
  });
}

function defaultContainer(): ServiceWorkerContainerLike | undefined {
  return typeof navigator !== "undefined" &&
    window.isSecureContext &&
    "serviceWorker" in navigator
    ? navigator.serviceWorker
    : undefined;
}

/**
 * Управляемое обновление приложения.
 *
 * Новая версия service worker устанавливается в фоне и ждёт. Применяется она
 * только по команде `apply()`: обновление перезагружает страницу, и решать,
 * когда это уместно, должен человек, а не браузер.
 */
export class ServiceWorkerUpdater extends SnapshotStore<UpdateSnapshot> {
  readonly #container: ServiceWorkerContainerLike | undefined;
  readonly #reload: () => void;
  readonly #target: Pick<Window, "addEventListener" | "removeEventListener">;
  #registration: ServiceWorkerRegistration | undefined;
  #hadController = false;
  #applyTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(options: ServiceWorkerUpdaterOptions = {}) {
    const container = options.container ?? defaultContainer();
    super({
      supported: container !== undefined,
      registered: false,
      status: "idle",
      checking: false,
      lastCheckedAt: null,
    });
    this.#container = container;
    this.#reload = options.reload ?? (() => window.location.reload());
    this.#target = options.target ?? window;
  }

  /** Регистрирует service worker и следит за появлением новой версии. */
  async start(): Promise<() => void> {
    const container = this.#container;
    if (container === undefined) return () => undefined;

    this.#hadController = container.controller !== null;
    const onControllerChange = () => this.#onControllerChange();
    container.addEventListener("controllerchange", onControllerChange);

    const recheck = () => void this.check();
    const onVisibility = () => {
      if (document.visibilityState === "visible") recheck();
    };
    this.#target.addEventListener("online", recheck);
    document.addEventListener("visibilitychange", onVisibility);
    const timer = setInterval(recheck, CHECK_INTERVAL_MS);
    const stop = () => {
      container.removeEventListener("controllerchange", onControllerChange);
      this.#target.removeEventListener("online", recheck);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(timer);
      clearTimeout(this.#applyTimer);
    };

    try {
      const registration = await registerServiceWorker(container);
      this.#registration = registration;
      this.update({ registered: true });
      // Первая установка не считается обновлением: сравнивать не с чем.
      if (registration.waiting !== null && container.controller !== null) {
        this.update({ status: "available" });
      }
      this.#watch(registration.installing);
      registration.addEventListener("updatefound", () =>
        this.#watch(registration.installing),
      );
    } catch {
      // Регистрация не удалась (нет сети при первом запуске, политика браузера).
      // Приложение работает и без неё: остаётся только кеш и фоновые уведомления.
    }

    return stop;
  }

  /** Спрашивает сервер, нет ли новой версии. */
  async check(): Promise<void> {
    const registration = this.#registration;
    if (registration === undefined || this.current.checking) return;
    this.update({ checking: true });
    try {
      await registration.update();
      this.update({ lastCheckedAt: Date.now() });
    } catch {
      // Нет связи или сервер недоступен: проверим в следующий раз.
    } finally {
      this.update({ checking: false });
    }
  }

  /**
   * Применяет скачанную версию: новый service worker берёт управление, и
   * страница перезагружается. Вызывать после того, как человек согласился.
   */
  apply(): boolean {
    const waiting = this.#registration?.waiting;
    if (waiting === null || waiting === undefined) return false;
    this.update({ status: "applying" });
    waiting.postMessage({ type: "SKIP_WAITING" });
    // Если смена не произошла (service worker завершился), не оставляем
    // человека с вечным «Обновляем…».
    this.#applyTimer = setTimeout(() => {
      if (this.current.status === "applying") {
        this.update({ status: "available" });
      }
    }, APPLY_TIMEOUT_MS);

    return true;
  }

  /** Перезагрузка вкладки, которая осталась на старой версии после обновления в другой. */
  reload(): void {
    this.#reload();
  }

  #watch(worker: ServiceWorker | null): void {
    if (worker === null) return;
    worker.addEventListener("statechange", () => {
      // «installed» при уже работающем service worker — это и есть новая
      // версия, ожидающая решения. Без прежнего контроллера это первая установка.
      if (worker.state === "installed" && this.#container?.controller) {
        this.update({ status: "available" });
      }
    });
  }

  #onControllerChange(): void {
    if (!this.#hadController) {
      // Первый service worker забрал управление у страницы, загруженной без него.
      this.#hadController = true;
      return;
    }
    if (this.current.status === "applying") {
      clearTimeout(this.#applyTimer);
      this.#reload();
      return;
    }
    this.update({ status: "reload-recommended" });
  }
}
